import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpService } from '../core/services/http.service';
import {
  WellsSummaryResponse,
  PerformanceSummaryResponse,
  NptSummaryResponse,
  TimeLogResponse,
} from '../models';

// Re-export for consumers that imported types from here
export type {
  WellsSummaryResponse,
  PerformanceSummaryResponse,
  NptSummaryResponse,
  TimeLogResponse,
  PerformancePhase,
  NptEvent,
  NptCause,
  TimeLogEntry,
} from '../models';

// ── Service ──────────────────────────────────────────────────────────────────

@Injectable({ providedIn: 'root' })
export class WellDataApiService {
  constructor(private httpService: HttpService) {}

  // ── API Methods: Accept either single well ID or array of well IDs ──────────

  /**
   * GET /api/v1/wells-summary/{well_id} for single well
   * POST /api/v1/wells-summary for multiple wells
   * Accepts: wellId as string (single) or string[] (multiple)
   */
  getWellsSummary(wellId: string | string[]): Observable<WellsSummaryResponse> {
    return this.httpService.getWellsSummary(wellId);
  }

  /**
   * GET /api/v1/performance-summary/{well_id} for single well
   * POST /api/v1/performance-summary for multiple wells
   * Accepts: wellId as string (single) or string[] (multiple)
   */
  getPerformanceSummary(wellId: string | string[]): Observable<PerformanceSummaryResponse> {
    return this.httpService.getPerformanceSummary(wellId);
  }

  /**
   * GET /api/v1/npt-summary/{well_id} for single well
   * POST /api/v1/npt-summary for multiple wells
   * Accepts: wellId as string (single) or string[] (multiple)
   */
  getNptSummary(wellId: string | string[]): Observable<NptSummaryResponse> {
    return this.httpService.getNptSummary(wellId);
  }

  /**
   * GET /api/v1/time-log/{well_id}/{job_id} for single well
   * POST /api/v1/time-log for multiple wells
   * Accepts: wellId as string (single) or string[] (multiple), jobId as string (single) or string[] (multiple)
   */
  getTimeLog(wellId: string | string[], jobId?: string | string[]): Observable<TimeLogResponse> {
    return this.httpService.getTimeLog(wellId, jobId);
  }

  /**
   * POST /api/v1/daily-report with well_ids and job_ids in payload
   * Returns data[] with WellID, WellName, JobID, dailyReportEntries[] (ReportStartDate, Summary)
   */
  getDailyReport(wellIds: string[], jobIds: string[]): Observable<any> {
    return this.httpService.getDailyReport(wellIds, jobIds);
  }

  /**
   * GET /api/v1/causal-summary/{well_id} for single well
   * POST /api/v1/causal-summary for multiple wells
   * Accepts: wellId as string (single) or string[] (multiple)
   */
  getCausalSummary(wellId: string | string[]): Observable<any> {
    return this.httpService.getCausalSummary(wellId);
  }

  /**
   * GET /api/v1/wells-list?field_name=X for single field
   * POST /api/v1/wells-list with array in body for multiple fields
   * Returns list of wells for a given field or multiple fields.
   */
  getWellsList(fieldName: string | string[]): Observable<any> {
    return this.httpService.getWellsList(fieldName);
  }

  /**
   * GET /api/v1/fields
   * Returns list of all available fields
   */
  getFieldsList(): Observable<any> {
    return this.httpService.getFieldsList();
  }

  /**
   * POST /api/v1/lookback-aggregate
   * Get aggregated lookback data for multiple wells across multiple sections
   * Accepts: wellIds (string[]) and include_sections (string[])
   */
  getLookbackAggregate(wellIds: string[], includeSections: string[]): Observable<any> {
    return this.httpService.getLookbackAggregate(wellIds, includeSections);
  }
}
